import { useEffect, useState } from 'react';
import { api } from 'services/api';

// Receipt link of the payment gateway (`{gateway}/api/receipts/{token}`): with `download=1` the PDF is saved as a file
// (`receipt-{invoiceNumber}.pdf`) instead of being opened in the browser.
export const receiptDownloadUrl = (receiptUrl: string): string =>
	`${receiptUrl}${receiptUrl.includes('?') ? '&' : '?'}download=1`;

// Starts the download (the gateway answers with Content-Disposition: attachment, the page stays where it is).
export const downloadPaymentReceipt = (receiptUrl: string): void => {
	const link = document.createElement('a');
	link.href = receiptDownloadUrl(receiptUrl);
	link.rel = 'noopener';
	document.body.appendChild(link);
	link.click();
	link.remove();
};

// Payment statuses of invoice_list_info that can have an online payment (and so a receipt).
const PAID_STATUSES = new Set(['paid', 'partially_paid']);

export const canHavePaymentReceipt = (paymentStatus: string | null | undefined): boolean =>
	!!paymentStatus && PAID_STATUSES.has(paymentStatus);

/**
 * Receipts of invoices paid online through the payment gateway (EAS invoice id → receipt link), one request for the given ids.
 * Invoices paid otherwise are absent; a failed request leaves the map empty (the pages work without the "receipt" action).
 */
const NO_RECEIPTS: Map<number, string> = new Map();

export const usePaymentReceipts = (invoiceIds: number[]): Map<number, string> => {
	// the receipts are kept together with the ids they were loaded for: other ids → nothing until their answer arrives
	const [loaded, setLoaded] = useState<{ key: string; receipts: Map<number, string> }>({
		key: '',
		receipts: NO_RECEIPTS,
	});
	const key = invoiceIds.join(',');

	useEffect(() => {
		if (!key) return undefined;
		let cancelled = false;
		api.paymentGateway
			.getInvoiceReceipts(key.split(',').map(Number))
			.then((response) => {
				if (!cancelled) {
					setLoaded({
						key,
						receipts: new Map(response.items.map((item) => [item.invoiceId, item.receiptUrl])),
					});
				}
			})
			.catch(() => {
				if (!cancelled) setLoaded({ key, receipts: NO_RECEIPTS });
			});
		return () => {
			cancelled = true;
		};
	}, [key]);

	return key && loaded.key === key ? loaded.receipts : NO_RECEIPTS;
};
